import { createSelector } from 'reselect';

import {
  getSubscriptionsSelector,
  getUnifiedAndUsageSubscriptionsSelector,
  hasActiveSubscriptionsSelector,
} from '../subscriptions/selectors';
import {
  ADDON_PROJECTS_PAID_THRESHOLD,
  PRODUCT_API_TESTING,
  PRODUCT_LIVE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_VDC,
  PRODUCT_USAGE,
  TESTING_TYPES,
  CONCURRENCY_KEY,
  PROJECTS_KEY,
  CREDIT_BLOCKS_KEY,
} from './../../constants';

const getStore = (store) => store.apps.billing.purchase;
const getCheckoutStore = (store) => store.apps.billing.checkout;
const getUser = (store) => store.user;

const filterPickedPlans = (productType) => (
  productType[CONCURRENCY_KEY] > 0 ||
  productType[PROJECTS_KEY] >= ADDON_PROJECTS_PAID_THRESHOLD ||
  productType[CREDIT_BLOCKS_KEY] > 0
);

const BASE_PRODUCTS = [PRODUCT_LIVE, PRODUCT_RDC, PRODUCT_VDC];

export const orderItemsSelector = createSelector(
  [(store) => getStore(store).get('configuration'), (store) => getStore(store).get('currentWebAndMobileTab')],
  (configuration, currentWebAndMobileTab) => (
    configuration
      .filter((value, key) => filterPickedPlans(value) && (!BASE_PRODUCTS.includes(key) || (
        currentWebAndMobileTab === TESTING_TYPES.LIVE ?
          (key === PRODUCT_LIVE) :
          ([PRODUCT_RDC, PRODUCT_VDC].includes(key))
      )))
  ),
);

export const changedOrderItemsSelector = createSelector(
  orderItemsSelector, getUnifiedAndUsageSubscriptionsSelector,
  (orderItems, subscriptions) => {
    if (!subscriptions?.length || !orderItems) return orderItems;
    return orderItems
      .filter((value, key) => subscriptions.every(
        (subscription) => !subscription.isEqualToPlanConfiguration(key, value),
      ));
  },
);

export const getIsContinueBlocked = createSelector(
  changedOrderItemsSelector,
  (orderItems) => orderItems.isEmpty(),
);

export const purchaseTypeSelector = createSelector(
  getCheckoutStore, hasActiveSubscriptionsSelector,
  (checkout, hasActiveSubscriptions) => {
    const preview = checkout.get('checkoutPreview');

    if (!hasActiveSubscriptions) {
      return 'New Subscription';
    }
    return preview.balance > 0 ? 'Upgrade' : 'Downgrade';
  },
);

export const getCurrentWebAndMobileTab = createSelector(
  getStore,
  (store) => store.get('currentWebAndMobileTab'),
);

export const getUsageConfigSelector = createSelector(
  getStore,
  (store) => store.getIn(['configuration', PRODUCT_USAGE]),
);

export const purchaseContainerSelector = createSelector(
  getStore,
  getSubscriptionsSelector,
  getCurrentWebAndMobileTab,
  orderItemsSelector,
  getUser,
  getIsContinueBlocked,
  (
    store,
    activeSubscriptions,
    currentWebAndMobileTab,
    orderItems,
    user,
    isContinueBlocked,
  ) => ({
    activeSubscriptions,
    currentWebAndMobileTab,
    initialized: store.get('initialized'),
    isContinueBlocked,
    liveConfig: store.getIn(['configuration', PRODUCT_LIVE]),
    mobileQualityConfig: store.getIn(['configuration', PRODUCT_MOBILE_QUALITY]),
    apiTestingConfig: store.getIn(['configuration', PRODUCT_API_TESTING]),
    orderItems,
    rdcConfig: store.getIn(['configuration', PRODUCT_RDC]),
    user,
    vdcConfig: store.getIn(['configuration', PRODUCT_VDC]),
  }),
);
